import { FormEvent, useState, useTransition } from "react";
import {
  activateUser,
  cleanupOldData,
  deactivateUser,
  toggleMaintenanceMode,
} from "@/lib/api";

type Message = {
  tone: "success" | "error";
  text: string;
};

export function AdminActionsPanel() {
  const [isPending, startTransition] = useTransition();
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(90);
  const [userId, setUserId] = useState("");
  const [message, setMessage] = useState<Message | null>(null);

  const showMessage = (tone: "success" | "error", text: string) => {
    setMessage({ tone, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleMaintenanceToggle = () => {
    startTransition(async () => {
      const response = await toggleMaintenanceMode(!maintenanceEnabled);
      if (!response) {
        showMessage("error", "Failed to update maintenance mode.");
        return;
      }
      setMaintenanceEnabled((prev) => !prev);
      showMessage(
        "success",
        `Maintenance mode ${!maintenanceEnabled ? "enabled" : "disabled"}.`,
      );
    });
  };

  const handleCleanup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      const response = await cleanupOldData(cleanupDays);
      if (!response) {
        showMessage("error", "Cleanup job failed.");
        return;
      }
      showMessage(
        "success",
        `Cleanup executed. Deleted records: ${JSON.stringify(response)}`,
      );
    });
  };

  const handleDeactivate = () => {
    if (!userId.trim()) {
      showMessage("error", "Provide a user ID.");
      return;
    }
    startTransition(async () => {
      const response = await deactivateUser(userId.trim());
      if (!response) {
        showMessage("error", "Failed to deactivate user.");
        return;
      }
      showMessage("success", `User ${userId.trim()} deactivated.`);
    });
  };

  const handleActivate = () => {
    if (!userId.trim()) {
      showMessage("error", "Provide a user ID.");
      return;
    }
    startTransition(async () => {
      const response = await activateUser(userId.trim());
      if (!response) {
        showMessage("error", "Failed to activate user.");
        return;
      }
      showMessage("success", `User ${userId.trim()} activated.`);
    });
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Administrative actions
          </h3>
          <p className="text-sm text-neutral-500">
            Trigger maintenance routines and manage user access.
          </p>
        </div>
      </div>

      {message ? (
        <p
          className={`mt-4 rounded-md border px-4 py-2 text-sm ${
            message.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-600"
          }`}
        >
          {message.text}
        </p>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <h4 className="text-sm font-semibold text-neutral-800">
            Maintenance mode
          </h4>
          <p className="mt-1 text-xs text-neutral-500">
            Toggle to block quiz creation and room launches during planned
            downtime.
          </p>
          <button
            type="button"
            onClick={handleMaintenanceToggle}
            disabled={isPending}
            className={`mt-3 inline-flex w-full items-center justify-center rounded-md px-3 py-2 text-sm font-medium ${
              maintenanceEnabled
                ? "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100"
                : "bg-neutral-900 text-white hover:bg-neutral-800"
            } disabled:opacity-70`}
          >
            {isPending ? "Working..." : maintenanceEnabled ? "Disable" : "Enable"}
          </button>
        </div>

        <form
          onSubmit={handleCleanup}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <h4 className="text-sm font-semibold text-neutral-800">
            Data cleanup
          </h4>
          <p className="mt-1 text-xs text-neutral-500">
            Remove records older than the configured retention window.
          </p>
          <label className="mt-3 flex items-center justify-between gap-3 text-xs text-neutral-600">
            Days to keep
            <input
              type="number"
              min={7}
              max={365}
              value={cleanupDays}
              onChange={(event) => setCleanupDays(Number(event.target.value))}
              className="w-20 rounded-md border border-neutral-200 px-2 py-1 text-xs focus:border-neutral-400 focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={isPending}
            className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-neutral-900 px-3 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
          >
            {isPending ? "Processing..." : "Run cleanup"}
          </button>
        </form>

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <h4 className="text-sm font-semibold text-neutral-800">
            User access
          </h4>
          <p className="mt-1 text-xs text-neutral-500">
            Activate or deactivate a user account.
          </p>
          <input
            type="text"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            placeholder="user-id"
            className="mt-3 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
          />
          <div className="mt-3 flex gap-3">
            <button
              type="button"
              onClick={handleDeactivate}
              disabled={isPending}
              className="flex-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-70"
            >
              {isPending ? "Working..." : "Deactivate"}
            </button>
            <button
              type="button"
              onClick={handleActivate}
              disabled={isPending}
              className="flex-1 rounded-md bg-neutral-900 px-3 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {isPending ? "Working..." : "Activate"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}


